import { Icon } from "@blueprintjs/core";
import { FilePond } from "react-filepond";
import { FGCheckboxInput, FGMaskInput, FGNumberInput, FieldGroup, useFGFormik } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { CoupleInbreedingApi, EDogSex } from "../../../../api";
import { FGDogSearchInput, SmallFormGenerator } from "../../../../components";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useRouter, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface IDogInbreedingVerificationTabProps {
  broodRequestId?: number;
  motherId?: number;
  fatherIds?: number[];
  redirectOnSave: boolean;
  onSaved?: () => void;
  isDrupal: boolean;
}

export const DogInbreedingVerificationTab: React.FunctionComponent<IDogInbreedingVerificationTabProps> = ({
  broodRequestId,
  motherId,
  fatherIds,
  redirectOnSave,
  isDrupal,
  onSaved,
}) => {
  const { t } = useTl();
  const router = useRouter();
  const api = useApiService(CoupleInbreedingApi);
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      broodRequestFatherInbreedings: Yup.array().of(
        Yup.object().shape({
          fatherId: Yup.number().nullable().required(t(ETLCodes.Required)),
          motherId: Yup.number().nullable().required(t(ETLCodes.Required)),
          inbreedingPercentage: Yup.number()
            .nullable()
            .min(0, t(ETLCodes.Between0And100))
            .max(100, t(ETLCodes.Between0And100)),
        })
      ),
    });
  }, [api, t]);
  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi(
    {
      getApiFn: async (id = broodRequestId) => {
        if (broodRequestId > 0) {
          const coupleInbreedings = await api.getBroodRequestInbreeding(broodRequestId);
          return { broodRequestFatherInbreedings: coupleInbreedings };
        } else {
          const coupleInbreedings = await api.getCouplesInbreeding(motherId, fatherIds);
          return { broodRequestFatherInbreedings: coupleInbreedings };
        }
      },
      saveApiFn: async (d) => {
        for (const c of d.broodRequestFatherInbreedings) {
          try {
            let file = undefined;
            if (c["inbreedingReport"]) {
              file = {
                data: c["inbreedingReport"].file,
                fileName: c["inbreedingReport"].filename,
              };
            }
            await api.save(
              c.coupleInbreedingId ?? 0,
              c.fatherId,
              c.motherId,
              c.inbreedingPercentage,
              file,
              c.validated
            );
          } catch (e) {
            console.error("error", e);
          }
        }
        if (onSaved) {
          onSaved();
        }
        if (redirectOnSave) router.push(`${ERoutes.broodRequest}/${broodRequestId}/dogInbreedingConfirmation/edit`);
        return 0;
      },
    },
    []
  );

  const { openNewTab } = useWindow();
  const formik = useFGFormik({
    initialValues: data,
    onSubmit: saveItem,
    validationSchema: FormSchema,
  });

  return (
    <SmallFormGenerator
      formik={formik}
      initialValues={data}
      onSubmit={saveItem}
      editMode={true}
      validationSchema={FormSchema}
      loading={loading}
      onDelete={deleteItem}
      saving={saving}
      deleting={deleting}
      enableDirtyCheck={false}
    >
      {formik?.values?.broodRequestFatherInbreedings?.map((d, i) => (
        <FieldGroup
          fieldsetProps={{
            title: t(ETLCodes.FatherInbreeding) + " : " + (i + 1),
          }}
        >
          <FGMaskInput
            cleaveOptions={{
              numeralThousandsGroupStyle: "thousand",
              numeralDecimalMark: ".",
              delimiter: ",",
            }}
            name={`broodRequestFatherInbreedings[${i}].inbreedingPercentage`}
            label={t(ETLCodes.Inbreeding)}
            rightElement={<Icon icon={"percentage"} />}
            readonly={isDrupal}
          />
          <FGDogSearchInput
            name={`broodRequestFatherInbreedings[${i}].fatherId`}
            defaultSearch={{ sex: EDogSex.Male }}
            label={t(ETLCodes.Father)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            requiredMark
            readonly
          />
          <FGDogSearchInput
            name={`broodRequestFatherInbreedings[${i}].motherId`}
            defaultSearch={{ sex: EDogSex.Female }}
            label={t(ETLCodes.Mother)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            requiredMark
            readonly
          />
          {!isDrupal && (
            <FGCheckboxInput name={`broodRequestFatherInbreedings[${i}].validated`} label={t(ETLCodes.Validated)} />
          )}
          <FilePond
            labelIdle={t(ETLCodes.FilePondDragOrSelect)}
            onupdatefiles={(f) => {
              formik.setFieldValue(`broodRequestFatherInbreedings[${i}].inbreedingReport`, f.length > 0 ? f[0] : null);
            }}
          />
        </FieldGroup>
      ))}
    </SmallFormGenerator>
  );
};
